/**
 * Legacy Quests Management Component
 * All 60 Legacy Quests that feed the Flutter app - editable and updatable
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Skeleton,
  Autocomplete,
} from '@mui/material';
import {
  Psychology as QuestIcon,
  Assessment as AnalyticsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import AdminEditDialogShell from '../shared/AdminEditDialogShell';
import PromptField from '../shared/PromptField';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import apiService from '../../services/api';
import MetricCard from '../common/MetricCard';
import StatusBadge from '../common/StatusBadge';
import { colors, spacing } from '../../theme/designTokens';

interface LegacyQuest {
  id: number;
  title: string;
  description: string;
  hint: string;
  level: number;
  category: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  estimatedTime: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  completionCount: number;
  averageRating: number;
}

interface QuestAnalytics {
  totalQuests: number;
  activeQuests: number;
  avgCompletionRate: number;
  avgRating: number;
  questsByLevel: Array<{
    level: number;
    count: number;
  }>;
  questsByCategory: Array<{
    category: string;
    count: number;
  }>;
  topQuests: LegacyQuest[];
}

interface QuestFormData {
  title: string;
  description: string;
  hint: string;
  level: number;
  category: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  estimatedTime: number;
  isActive: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`quest-tabpanel-${index}`}
      aria-labelledby={`quest-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const DIFFICULTY_COLORS = {
  Easy: 'success',
  Medium: 'warning',
  Hard: 'error',
} as const;

const CATEGORIES = [
  'Brain Health',
  'Memory Enhancement',
  'Legacy Building',
  'Family Connection',
  'Creative Expression',
  'Life Stories',
  'Personal Growth',
  'Historical Reflection',
];

export const LegacyQuests: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const [tabValue, setTabValue] = useState(0);
  const [quests, setQuests] = useState<LegacyQuest[]>([]);
  const [analytics, setAnalytics] = useState<QuestAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalQuests, setTotalQuests] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingQuest, setEditingQuest] = useState<LegacyQuest | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<QuestFormData>({
    defaultValues: {
      title: '',
      description: '',
      hint: '',
      level: 1,
      category: '',
      difficulty: 'Easy',
      estimatedTime: 15,
      isActive: true,
    },
  });

  const loadQuests = async () => {
    try {
      setLoading(true);
      const response = await apiService.getLegacyQuests({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });

      const questsData = response.quests || response.data?.quests || response.data || [];
      const mappedQuests = questsData.map(quest => ({
        id: quest.quest_id || quest.id,
        title: quest.title,
        description: quest.quest_description || quest.description,
        hint: quest.hint || '',
        level: quest.level,
        category: quest.category,
        difficulty: quest.difficulty_level || 'Easy',
        estimatedTime: quest.time_to_complete || quest.estimated_completion_time || 15,
        isActive: quest.is_active !== false,
        createdAt: quest.created_at,
        updatedAt: quest.updated_at,
        completionCount: quest.completion_count || 0,
        averageRating: quest.average_rating || 0,
      }));

      setQuests(mappedQuests);
      setTotalQuests(response.total || response.data?.totalCount || mappedQuests.length);
    } catch (error) {
      console.error('Failed to load quests:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load legacy quests',
      }));
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await apiService.getLegacyQuestAnalytics();
      setAnalytics(response);
    } catch (error) {
      console.error('Failed to load quest analytics:', error);
      // Set fallback analytics
      setAnalytics({
        totalQuests: quests.length,
        activeQuests: quests.filter(q => q.isActive).length,
        avgCompletionRate: quests.reduce((acc, q) => acc + q.completionCount, 0) / Math.max(quests.length, 1),
        avgRating: quests.reduce((acc, q) => acc + (q.averageRating || 0), 0) / Math.max(quests.length, 1),
        questsByLevel: [],
        questsByCategory: [],
        topQuests: quests.slice(0, 5),
      });
    }
  };

  const handleCreateQuest = () => {
    reset({
      title: '',
      description: '',
      hint: '',
      level: 1,
      category: '',
      difficulty: 'Easy',
      estimatedTime: 15,
      isActive: true,
    });
    setEditingQuest(null);
    setIsEditModalOpen(true);
  };

  const handleEditQuest = (quest: LegacyQuest) => {
    reset({
      title: quest.title,
      description: quest.description,
      hint: quest.hint,
      level: quest.level,
      category: quest.category,
      difficulty: quest.difficulty,
      estimatedTime: quest.estimatedTime,
      isActive: quest.isActive,
    });
    setEditingQuest(quest);
    setIsEditModalOpen(true);
  };

  const handleDeleteQuest = async (questId: number) => {
    if (!window.confirm('Are you sure you want to delete this quest?')) {
      return;
    }

    try {
      await apiService.deleteLegacyQuest(questId);
      dispatch(addNotification({
        type: 'success',
        message: 'Quest deleted successfully',
      }));
      await loadQuests();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to delete quest',
      }));
    }
  };

  const onSubmit = async (data: QuestFormData) => {
    try {
      if (editingQuest) {
        await apiService.updateLegacyQuest(editingQuest.id, data);
        dispatch(addNotification({
          type: 'success',
          message: 'Quest updated successfully',
        }));
      } else {
        await apiService.createLegacyQuest(data);
        dispatch(addNotification({
          type: 'success',
          message: 'Quest created successfully',
        }));
      }

      setIsEditModalOpen(false);
      await loadQuests();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: `Failed to ${editingQuest ? 'update' : 'create'} quest`,
      }));
    }
  };

  const handleRefresh = async () => {
    await loadQuests();
    await loadAnalytics();
    dispatch(addNotification({
      type: 'success',
      message: 'Quest data refreshed',
    }));
  };

  useEffect(() => {
    loadQuests();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (quests.length > 0) {
      loadAnalytics();
    }
  }, [quests]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <Box sx={{ p: spacing[6], bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: spacing[8] }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: spacing[3],
                mb: spacing[2]
              }}
            >
              <QuestIcon sx={{ fontSize: 40, color: colors.content.main }} />
              Legacy Quests
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
              Manage all 60 Legacy Quests that feed the Flutter app. All changes sync in real-time
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: spacing[2] }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateQuest}
            >
              Add Quest
            </Button>
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 2, borderColor: colors.content.main, mb: spacing[6] }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="quest management tabs"
          TabIndicatorProps={{
            style: {
              backgroundColor: colors.content.main,
              height: 3,
            }
          }}
        >
          <Tab
            label="Quests"
            icon={<QuestIcon />}
            iconPosition="start"
            sx={{
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 600,
              '&.Mui-selected': {
                color: colors.content.main,
              }
            }}
          />
          <Tab
            label="Quest Analytics"
            icon={<AnalyticsIcon />}
            iconPosition="start"
            sx={{
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 600,
              '&.Mui-selected': {
                color: colors.content.main,
              }
            }}
          />
        </Tabs>

        {/* Quests Tab */}
        <TabPanel value={tabValue} index={0}>
          <Card>
            <CardContent>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Quest Details</TableCell>
                      <TableCell>Level</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Difficulty</TableCell>
                      <TableCell>Time (min)</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Completions</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: rowsPerPage }).map((_, index) => (
                        <TableRow key={index}>
                          {Array.from({ length: 9 }).map((_, cellIndex) => (
                            <TableCell key={cellIndex}>
                              <Skeleton variant="text" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      quests.map((quest) => (
                        <TableRow key={quest.id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {quest.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                maxWidth: 300
                              }}>
                                {quest.description}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`Level ${quest.level}`}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{quest.category}</Typography>
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              status={
                                quest.difficulty === 'Easy' ? 'success' :
                                quest.difficulty === 'Medium' ? 'warning' :
                                'error'
                              }
                              label={quest.difficulty}
                              variant="filled"
                              size="sm"
                            />
                          </TableCell>
                          <TableCell>{quest.estimatedTime}</TableCell>
                          <TableCell>
                            <StatusBadge
                              status={quest.isActive ? 'active' : 'inactive'}
                              label={quest.isActive ? 'Active' : 'Inactive'}
                              variant="filled"
                              size="sm"
                            />
                          </TableCell>
                          <TableCell>{quest.completionCount}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <StarIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                              <Typography variant="body2">
                                {quest.averageRating ? quest.averageRating.toFixed(1) : '0.0'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleEditQuest(quest)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteQuest(quest.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalQuests}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </CardContent>
          </Card>
        </TabPanel>

        {/* Quest Analytics Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            {/* Summary Stats */}
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Quests"
                value={analytics?.totalQuests || 0}
                icon="psychology"
                color="content"
                loading={!analytics}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Active Quests"
                value={analytics?.activeQuests || 0}
                icon="trending_up"
                color="default"
                loading={!analytics}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Avg Completion"
                value={Math.round(analytics?.avgCompletionRate || 0)}
                icon="people"
                color="content"
                loading={!analytics}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Avg Rating"
                value={(analytics?.avgRating || 0).toFixed(1)}
                icon="star"
                color="content"
                loading={!analytics}
              />
            </Grid>

            {/* Charts */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quests by Level
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics?.questsByLevel || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="level" />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="count" fill={theme.palette.primary.main} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quests by Category
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics?.questsByCategory || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {(analytics?.questsByCategory || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Box>

      {/* Edit/Create Quest Modal */}
      <AdminEditDialogShell
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        accentColor="#854F0B"
        typeInitial="Q"
        title={`${editingQuest ? 'Edit' : 'Create'} quest — ${watch('title') || 'Untitled'}`}
        subtitle="Legacy Quest brain health challenge · displayed in the Legacy Quest screen"
        formId="quest-edit-form"
        isDirty={isDirty}
        showActiveToggle
        isActive={watch('isActive')}
        onToggleActive={() => {
          const current = watch('isActive');
          reset({ ...watch(), isActive: !current }, { keepDirty: true });
        }}
      >
        <form id="quest-edit-form" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: 'Title is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Quest Title"
                      fullWidth
                      error={!!errors.title}
                      helperText={errors.title?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: 'Description is required' }}
                  render={({ field }) => (
                    <PromptField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                      label="DESCRIPTION"
                      helperText="Quest description shown to users in the Legacy Quest screen"
                      accentColor="#854F0B"
                      minRows={9}
                      error={!!errors.description}
                      errorMessage={errors.description?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="hint"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Hint (Optional)"
                      fullWidth
                      multiline
                      rows={2}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="level"
                  control={control}
                  rules={{ required: 'Level is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.level}>
                      <InputLabel>Level</InputLabel>
                      <Select {...field} label="Level">
                        {[1, 2, 3, 4, 5, 6, 7].map((level) => (
                          <MenuItem key={level} value={level}>Level {level}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="difficulty"
                  control={control}
                  rules={{ required: 'Difficulty is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.difficulty}>
                      <InputLabel>Difficulty</InputLabel>
                      <Select {...field} label="Difficulty">
                        <MenuItem value="Easy">Easy</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="Hard">Hard</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="category"
                  control={control}
                  rules={{ required: 'Category is required' }}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      options={CATEGORIES}
                      freeSolo
                      onChange={(_, value) => field.onChange(value || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Category"
                          error={!!errors.category}
                          helperText={errors.category?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="estimatedTime"
                  control={control}
                  rules={{
                    required: 'Estimated time is required',
                    min: { value: 1, message: 'Time must be at least 1 minute' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Estimated Time (minutes)"
                      type="number"
                      fullWidth
                      error={!!errors.estimatedTime}
                      helperText={errors.estimatedTime?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </form>
      </AdminEditDialogShell>
    </Box>
  );
};

export default LegacyQuests;